import React, { useState, useEffect, useRef } from "react";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Divider,
  CloseButton,
  FieldWrapper,
  Input,
  ErrorMessage,
  DragAndDropLinkField,
  SwitchFieldRow,
  SwitchHelpButton,
} from "./CreateModal.styles";
import { CreateModalProps } from "./CreateModal.types";
import Button from "../../Buttons/Button";
import DragAndDropField from "../../DragAndDropField/DragAndDropField";
import { closeIcon as CloseIcon } from "assets/icons";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import Switch from "../../Switch";
import DropdownWithInput from "../../Dropdowns/DropdownWithInput";
import ReactDOM from "react-dom";
import DropdownWithObjects from "../../Dropdowns/DropdownWithObjects/DropdownWithObjects";
import DatePickerInput from "../../CalendarInput/CalendarInput";
import TimePickerInput from "../../TimePickerInput/TimePickerInput";
import Tooltip from "../../Tooltip/Tooltip";

import { logger } from "utils/logger";

const CreateModal: React.FC<CreateModalProps> = ({
  className,
  title,
  fields,
  onSubmit,
  submitButtonText = "Создать курс",
  hasDragAndDrop = false,
  onCancel,
  onRemoveFile,
  initialValues = {},
  files_from_storage = [],
  disableToggleSwitch = false,
  maxFiles = 5,
  externalErrors = {},
  isLoading = false,
  extraContent,
  externalSubmitDisabled = false,
}) => {
  logger.log("CreateModal: isLoading =", isLoading);
  const [formData, setFormData] = useState<Record<string, string>>(
    fields.reduce((acc, field) => {
      acc[field.name] = initialValues[field.name] || field.value || "";
      return acc;
    }, {} as Record<string, string>),
  );

  const [fileData, setFileData] = useState<File[] | null>(
    Array.isArray(initialValues.files) ? (initialValues.files as File[]) : null,
  );
  const [linkData, setLinkData] = useState<string>(initialValues.link || "");
  const [errors, setErrors] = useState<Record<string, string | null>>(externalErrors || {});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeTab, setActiveTab] = useState<string>("Файл");

  useEffect(() => {
    const allRequiredFieldsFilled = fields.every(
      (field) =>
        !field.required ||
        (field.name !== "files" &&
          field.name !== "link" &&
          formData[field.name]?.trim() !== "") ||
        (activeTab === "Файл" && fileData && fileData.length > 0) ||
        (activeTab === "Ссылка" && linkData.trim() !== ""),
    );
    setIsSubmitDisabled(!allRequiredFieldsFilled);
  }, [formData, fields, activeTab, fileData, linkData]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
    setLinkData(initialValues.video_url || "");
  }, [initialValues.video_url]);

  useEffect(() => {
    setErrors((prevErrors) => {
      if (JSON.stringify(prevErrors) !== JSON.stringify(externalErrors)) {
        return {
          ...prevErrors,
          ...externalErrors,
        };
      }
      return prevErrors;
    });
  }, [externalErrors]);

  const handleChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleTextareaChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleLinkChange = (value: string) => {
    setLinkData(value);
    if (errors.link) {
      setErrors({ ...errors, link: null });
    }
  };

  const handleToggleTab = (option: string) => {
    if (option === "Файл" && !fileData) {
      setFileData([]);
    }
    setActiveTab(option);
  };

  const handleSubmit = () => {
    const newErrors = fields.reduce<Record<string, string | null>>((acc, field) => {
      if (field.required && !formData[field.name]?.trim() && !(field.name === "files" || field.name === "link")) {
        acc[field.name] = "Это поле обязательно";
      }
      return acc;
    }, {});

    setErrors(newErrors);

    if (Object.values(newErrors).every((err) => !err)) {
      const dataToSubmit = {
        ...formData,
        files: fileData,
        video_url: linkData,
      };
      onSubmit(dataToSubmit);
    }
  };

  const modalContent = (
    <ModalOverlay className={className}>
      <ModalContent>
        <ModalHeader>
          <h2>{title}</h2>
          <CloseButton aria-label="Закрыть" onClick={onCancel}>
            <CloseIcon aria-hidden="true" />
          </CloseButton>
        </ModalHeader>
        <Divider />
        <ModalBody>
          <form onSubmit={(event) => event.preventDefault()}>
            {(errors.detail || errors.non_field_errors || errors.error) && (
              <ErrorMessage>{errors.detail || errors.non_field_errors || errors.error}</ErrorMessage>
            )}
            {fields.map(
              (field) =>
                (!hasDragAndDrop || field.name !== "link") && (
                  <FieldWrapper key={field.name}>
                    {errors[field.name] && <ErrorMessage>{errors[field.name]}</ErrorMessage>}
                    {field.type === "date" ? (
                      <DatePickerInput
                        value={formData[field.name] || ""}
                        placeholder={field.placeholder || ""}
                        onChange={(value: string) => handleChange(field.name, value)}
                      />
                    ) : field.type === "time" ? (
                      <TimePickerInput
                        value={formData[field.name] || ""}
                        placeholder={field.placeholder || ""}
                        onChange={(value: string) => handleChange(field.name, value)}
                      />
                    ) : field.type === "textarea" ? (
                      <Input
                        id={field.name}
                        as="textarea"
                        ref={textareaRef}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={(event) => handleTextareaChange(field.name, event.target.value)}
                        hasError={Boolean(errors[field.name])}
                      />
                    ) : field.type === "switch" ? (
                      <SwitchFieldRow>
                        <Switch
                          checked={formData[field.name] === "true"}
                          onChange={(checked) => handleChange(field.name, checked ? "true" : "false")}
                          label={field.label || field.placeholder || ""}
                          ariaLabel={field.label || field.placeholder || field.name}
                        />
                        {field.hint ? (
                          <Tooltip content={field.hint} placement="top" maxWidth="360px">
                            <SwitchHelpButton
                              type="button"
                              aria-label={`Что означает «${field.label || field.placeholder || field.name}»`}
                              onClick={(event) => event.currentTarget.focus()}
                            >
                              ?
                            </SwitchHelpButton>
                          </Tooltip>
                        ) : null}
                      </SwitchFieldRow>
                    ) : field.type === "selectWithInput" && field.options ? (
                      Array.isArray(field.options) &&
                      (typeof field.options[0] === "object" ? (
                        <DropdownWithObjects
                          options={field.options as { label: string; value: string }[]}
                          placeholder={field.placeholder || "Выберите значение"}
                          value={formData[field.name]}
                          onChange={(value) => handleChange(field.name, value)}
                        />
                      ) : (
                        <DropdownWithInput
                          options={field.options as string[]}
                          placeholder={field.placeholder || "Выберите значение"}
                          value={formData[field.name]}
                          onChange={(value) => handleChange(field.name, value)}
                        />
                      ))
                    ) : (
                      <Input
                        type={field.type}
                        min={field.type === "number" ? 1 : undefined}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={(event) => handleChange(field.name, event.target.value)}
                        hasError={Boolean(errors[field.name])}
                      />
                    )}
                  </FieldWrapper>
                ),
            )}

            {hasDragAndDrop && (
              <DragAndDropLinkField>
                {!disableToggleSwitch && (
                  <ToggleSwitch
                    options={["Ссылка", "Файл"]}
                    activeOption={activeTab}
                    onToggle={handleToggleTab}
                  />
                )}
                <div style={{ display: activeTab === "Файл" ? "block" : "none" }}>
                  <DragAndDropField
                    files={fileData}
                    onFileSelected={setFileData}
                    placeholder="Перетащите файлы сюда или нажмите, чтобы выбрать."
                    maxFiles={maxFiles}
                    files_from_storage={files_from_storage}
                    onRemoveFile={onRemoveFile}
                    key={files_from_storage?.map((file) => `${file.id}-${file.progress_percent}`).join(",") || "empty"}
                  />
                </div>
                <div style={{ display: activeTab === "Ссылка" ? "block" : "none" }}>
                  <FieldWrapper className="link-field">
                    {errors.video_url && <ErrorMessage>{errors.video_url}</ErrorMessage>}
                    <Input
                      type="text"
                      placeholder="Введите ссылку"
                      value={linkData}
                      onChange={(event) => handleLinkChange(event.target.value)}
                      hasError={Boolean(errors.link)}
                    />
                  </FieldWrapper>
                </div>
              </DragAndDropLinkField>
            )}

            {extraContent}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            disabled={isSubmitDisabled || externalSubmitDisabled || isLoading}
            onClick={handleSubmit}
            fullWidth
          >
            {isLoading ? "Сохранение..." : submitButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CreateModal;